import { useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import SlickSlider from 'react-slick';

import { visuallyHiddenStyle } from '@/components/common/utils';

const SliderDots = ({ dots, sliderHeading, slideItems, slideItemTitleKey }) => {
  return (
    <ul className="slick-dots" {...dots.props}>
      {dots.map(({ props, key }, index) => {
        const slideItemHeading = slideItems[index][slideItemTitleKey];
        return (
          <li key={key} {...props}>
            <button
              type="button"
              {...props.children.props}
              aria-current={props.className.includes('slick-active')}
            >
              Slide {index + 1}{' '}
              {dots.length !== slideItems.length || !slideItemHeading?.length
                ? sliderHeading
                : slideItemHeading}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const defaultSliderSettings = (
  heading: string,
  slideItems,
  slideItemTitleKey: string,
) => ({
  appendDots: (dots) => (
    <SliderDots
      dots={dots}
      sliderHeading={heading}
      slideItems={slideItems}
      slideItemTitleKey={slideItemTitleKey}
    />
  ),
});

export const Slider = ({
  settings,
  heading,
  slideItems,
  slideItemTitleKey,
  children,
  ...props
}) => {
  const sliderWrapperRef = useRef(null);
  const hasMultipleSlides = slideItems.length > 1;

  const initLinksTabIndex = () => {
    const inactiveSlides = sliderWrapperRef.current?.querySelectorAll(
      '.slick-slide:not(.slick-active)',
    );

    inactiveSlides?.forEach((inactiveSlide) => {
      const inactiveLinks = inactiveSlide.querySelectorAll('a');
      inactiveLinks?.forEach((link) => {
        link.tabIndex = -1;
      });
    });
  };

  useEffect(() => {
    setTimeout(() => {
      initLinksTabIndex();
    }, 100);
  }, []);

  const handleAfterChange = useCallback(() => {
    initLinksTabIndex();

    const currentSlide = sliderWrapperRef.current.querySelector(
      '.slick-slide.slick-current',
    );
    const currentLinks = currentSlide.querySelectorAll('a');
    currentLinks?.forEach((link) => {
      // eslint-disable-next-line no-param-reassign
      link.tabIndex = null;
    });
  }, []);

  return hasMultipleSlides ? (
    <div ref={sliderWrapperRef}>
      <StyledSlider
        {...props}
        {...defaultSliderSettings(heading, slideItems, slideItemTitleKey)}
        {...settings}
        afterChange={handleAfterChange}
      >
        {children}
      </StyledSlider>
    </div>
  ) : (
    children
  );
};

const StyledSlider = styled(SlickSlider)`
  /* Slider */
  position: relative;

  display: block;
  box-sizing: border-box;

  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  -webkit-touch-callout: none;
  -khtml-user-select: none;
  -ms-touch-action: pan-y;
  touch-action: pan-y;

  .slick-list {
    position: relative;

    display: block;
    overflow: hidden;

    margin: 0;
    padding: 0;
  }
  .slick-list:focus {
    outline: none;
  }
  .slick-list.dragging {
    cursor: pointer;
    cursor: hand;
  }

  > .slick-track,
  > .slick-list {
    /* transform: translate3d(0, 0, 0); */
  }

  .slick-track {
    position: relative;
    top: 0;
    left: 0;

    display: block;
    margin-left: auto;
    margin-right: auto;
  }
  .slick-track:before,
  .slick-track:after {
    display: table;
    content: '';
  }
  .slick-track:after {
    clear: both;
  }
  &.slick-loading .slick-track {
    visibility: hidden;
  }

  .slick-slide {
    display: none;
    float: left;
    height: 100%;
    min-height: 1px;
  }
  [dir='rtl'] .slick-slide {
    float: right;
  }
  .slick-slide img {
    display: block;
  }
  .slick-slide.slick-loading img {
    display: none;
  }
  .slick-slide.dragging img {
    pointer-events: none;
  }
  &.slick-initialized .slick-slide {
    display: block;
  }
  &.slick-loading .slick-slide {
    visibility: hidden;
  }
  &.slick-vertical .slick-slide {
    display: block;

    height: auto;

    border: 1px solid transparent;
  }
  .slick-arrow.slick-hidden {
    display: none;
  }
  .slick-arrow::before {
    ${visuallyHiddenStyle}
  }

  /* Arrows */
  .slick-prev,
  .slick-next {
    font-size: 0;
    line-height: 0;

    position: absolute;
    top: 50%;
  }

  .slick-prev {
    left: -25px;
  }
  [dir='rtl'] .slick-prev {
    right: -25px;
    left: auto;
  }

  .slick-next {
    right: -25px;
  }
  [dir='rtl'] .slick-next {
    right: auto;
    left: -25px;
  }

  /* Dots */
  .slick-dotted.slick-slider {
    margin-bottom: 30px;
  }

  .slick-dots {
    position: absolute;
    bottom: -25px;

    display: block;

    width: 100%;
    padding: 0;
    margin: 0;

    list-style: none;

    text-align: center;
  }
  .slick-dots li {
    position: relative;

    display: inline-block;

    width: 40px;
    height: 40px;
    margin: 0 5px;
    padding: 0;

    cursor: pointer;
  }
  .slick-dots li button {
    font-size: 0;
    line-height: 0;

    display: block;

    width: 40px;
    height: 40px;

    cursor: pointer;
    border-radius: 100%;

    color: transparent;
    border: 0;
    background: transparent;
  }

  .slick-dots li button:before {
    position: absolute;
    display: block;
    content: '';
    left: 50%;
    top: 50%;
    border-radius: 6px;
    transform: translate3d(-50%, -50%, 0);
    width: 12px;
    height: 12px;
    background: var(--surface-inverse-subtle);
    transition: background 100ms ease-in-out;
  }
  .slick-dots li.slick-active button:before {
    background: var(--surface-primary);
  }

  .slick-dots li button:hover:before,
  .slick-dots li button:focus:before {
    background: var(--surface-primary);
  }
`;
